import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import axios from '../../axios';
import config from '../../config';

import Ad from '../Objectinfo/Ad';
import { parseAd } from '../../utils/adUtils';
import { useLanguage } from '../../i18n/LanguageContext';

// Import ObjectForm CSS to get checkbox styling
import '../Objectinfo/ObjectForm.css';

function NewConversation({
  preselectedSellerId,
  preselectedObjectLink,
  streetName = '',
  onConversationCreated,
  onCancel,
  preselectedBrokerId
}) {
  const backend_url = config.backend_url;
  const history = useHistory();
  const { t } = useLanguage();

  // Determine user type from local storage: 'seller' or 'broker'
  const userType = localStorage.getItem('userType');

  const [otherSideList, setOtherSideList] = useState([]);
  const [selectedOtherUserId, setSelectedOtherUserId] = useState('');
  const [selectedUserDescription, setSelectedUserDescription] = useState('');
  const [conversationMessage, setConversationMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // New broker agreement fields
  const [expectedPrice, setExpectedPrice] = useState('');
  const [brokerFee, setBrokerFee] = useState('');
  const [contractDuration, setContractDuration] = useState('');
  const [terminationConditions, setTerminationConditions] = useState('');
  const [acceptTerms, setAcceptTerms] = useState(false);
  
  // Validation state
  const [formErrors, setFormErrors] = useState({});

  // We'll store any fetched sellerData, if relevant
  const [sellerData, setSellerData] = useState(null);

  // Optional: parse ad (not used below but left for your convenience)
  const ad = localStorage.getItem('ad');

  // 1) Determine a default front-end link for sellerData, or use the preselected link
  useEffect(() => {
    const sellerDataId = localStorage.getItem('sellerDataId');
    if (sellerDataId) {
      fetchSellerData(sellerDataId);
    }
  }, []);

  const fetchSellerData = async (sellerDataId) => {
    try {
      const response = await axios.get(`${config.backend_url}/api/seller-data/${sellerDataId}`, {
        withCredentials: true,
      });
      setSellerData(response.data);
    } catch (error) {
      console.error('Error fetching seller data:', error);
    }
  };

  // Function to ensure the link is fully qualified
  const makeFullLink = (link) => {
    if (!link) return '';
    // If the link starts with 'http' or 'https', assume it's already full
    if (/^https?:\/\//i.test(link)) {
      return link;
    }
    // Otherwise, prepend the origin
    return `${window.location.origin}${link}`;
  };

  const sellerDataId = localStorage.getItem('seller_data_id');
  const defaultLink = preselectedObjectLink
    ? makeFullLink(preselectedObjectLink)
    : sellerDataId
      ? `${window.location.origin}/seller-data/${sellerDataId}`
      : '';

  const [objectLink, setObjectLink] = useState(defaultLink);

  // 2) Fetch the list of "the other side" users (brokers if I'm a seller, sellers if I'm a broker)
  useEffect(() => {
    const fetchUserList = async () => {
      try {
        if (userType === 'seller') {
          // fetch brokers
          const response = await axios.get(`${backend_url}/api/brokers`, {
            withCredentials: true,
          });
          const brokersData = response.data;
          setOtherSideList(brokersData);
          if (preselectedBrokerId) {
            setSelectedOtherUserId(preselectedBrokerId.toString());
          } else if (brokersData.length > 0) {
            setSelectedOtherUserId(brokersData[0].id);
          }
        } else if (userType === 'broker') {
          // fetch sellers
          const response = await axios.get(`${backend_url}/api/sellers`, {
            withCredentials: true,
          });
          const sellersData = response.data;
          setOtherSideList(sellersData);
          if (preselectedSellerId) {
            setSelectedOtherUserId(preselectedSellerId.toString());
          } else if (sellersData.length > 0) {
            setSelectedOtherUserId(sellersData[0].id);
          }
        }
      } catch (error) {
        console.error('Error fetching users:', error);
      }
    };
    fetchUserList();
  }, [backend_url, userType, preselectedSellerId, preselectedBrokerId]);

  // 3) Fetch details about the selected user for display (their description, etc.)
  useEffect(() => {
    const fetchSelectedUser = async () => {
      try {
        if (selectedOtherUserId) {
          if (userType === 'seller') {
            const response = await axios.get(`${backend_url}/api/brokers/${selectedOtherUserId}`, {
              withCredentials: true,
            });
            setSelectedUserDescription(response.data.description || '');
          } else if (userType === 'broker') {
            const response = await axios.get(`${backend_url}/api/sellers/${selectedOtherUserId}`, {
              withCredentials: true,
            });
            setSelectedUserDescription(response.data.description || '');
          }
        }
      } catch (error) {
        console.error('Error fetching selected user:', error);
      }
    };

    fetchSelectedUser();
  }, [backend_url, selectedOtherUserId, userType]);

  // Validate form fields
  const validateForm = () => {
    const errors = {};
    
    // Validate message content
    if (conversationMessage && conversationMessage.length > 5000) {
      errors.message = t('conversation.tooLong');
    }
    
    // Validate object link
    if (objectLink) {
      if (objectLink.length > 2000) {
        errors.link = t('objectForm.validation.descriptionMax');
      }
      if (!/^https?:\/\/.+/.test(objectLink)) {
        errors.link = t('conversation.linkProtocol');
      }
    }
    
    // Only validate broker agreement fields if user is a broker
    if (userType === 'broker') {
      if (!acceptTerms) {
        errors.acceptTerms = t('conversation.acceptTerms');
      }
      if (!expectedPrice) {
        errors.expectedPrice = t('valuationChat.required');
      } else if (expectedPrice.length > 100) {
        errors.expectedPrice = t('conversation.chars', { count: expectedPrice.length });
      }
      
      if (!brokerFee) {
        errors.brokerFee = t('valuationChat.required');
      } else if (brokerFee.length > 100) {
        errors.brokerFee = t('conversation.chars', { count: brokerFee.length });
      }
      
      if (!contractDuration) {
        errors.contractDuration = t('valuationChat.required');
      } else if (contractDuration.length > 100) {
        errors.contractDuration = t('conversation.chars', { count: contractDuration.length });
      }
      
      if (!terminationConditions) {
        errors.terminationConditions = t('valuationChat.required');
      } else if (terminationConditions.length > 1000) {
        errors.terminationConditions = t('conversation.chars', { count: terminationConditions.length });
      }
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Input sanitization function
  const sanitizeInput = (input) => {
    if (!input) return '';
    // Remove null bytes and dangerous control characters, but preserve newlines (\n) and carriage returns (\r)
    return input.replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, '');
  };

  // Handle input changes with sanitization
  const handleMessageChange = (e) => {
    const sanitized = sanitizeInput(e.target.value);
    setConversationMessage(sanitized);
  };

  const handleLinkChange = (e) => {
    const sanitized = sanitizeInput(e.target.value);
    setObjectLink(sanitized);
  };

  const handleExpectedPriceChange = (e) => {
    const sanitized = sanitizeInput(e.target.value);
    setExpectedPrice(sanitized);
  };

  const handleBrokerFeeChange = (e) => {
    const sanitized = sanitizeInput(e.target.value);
    setBrokerFee(sanitized);
  };

  const handleContractDurationChange = (e) => {
    const sanitized = sanitizeInput(e.target.value);
    setContractDuration(sanitized);
  };

  const handleTerminationConditionsChange = (e) => {
    const sanitized = sanitizeInput(e.target.value);
    setTerminationConditions(sanitized);
  };

  // 4) Handle creating a conversation
  const handleNewConversation = async (e) => {
    e.preventDefault();
    
    if (!selectedOtherUserId) {
      alert(t('conversation.selectCounterparty'));
      return;
    }
    
    // Validate form if user is a broker
    if (userType === 'broker' && !validateForm()) {
      return;
    }

    setIsLoading(true);

    // The user's typed message - preserve line breaks, only check if effectively empty
    let userMessage = conversationMessage;
    // Check if message is effectively empty (only whitespace/newlines) for validation
    const isMessageEmpty = !userMessage.trim();
    
    const fullLink = makeFullLink(preselectedObjectLink || objectLink);

    // Build the message content - do NOT include broker agreement details (backend handles this)
    let messageContent = '';
    
    if (userType === 'broker') {
      // For brokers: send street name first, backend will add agreement details, then user message comes after
      if (streetName) {
        messageContent = streetName;
      }
      // User message will be handled separately and added after agreement details by backend
    } else {
      // For sellers: combine street name and message normally  
      if (streetName) {
        messageContent = `${streetName}`;
        if (userMessage && !isMessageEmpty) {
          messageContent += `, ${userMessage}`;
        }
      } else if (userMessage && !isMessageEmpty) {
        messageContent = userMessage;
      }
    }

    // Build payload for the message
    let payload = {
      message: messageContent
    };

    // Include the other side's ID
    if (userType === 'seller') {
      payload.brokerId = selectedOtherUserId;
    } else if (userType === 'broker') {
      payload.sellerId = selectedOtherUserId;
      
      // Add broker agreement details to payload
      payload.expected_price = expectedPrice;
      payload.broker_fee = brokerFee;
      payload.contract_duration = contractDuration;
      payload.termination_conditions = terminationConditions;
      
      // Send user message separately for proper formatting (preserve line breaks)
      if (userMessage && !isMessageEmpty) {
        payload.userMessage = userMessage;
      }
    }

    // Include the link as a separate field
    payload.link = fullLink;

    try {
      // Create the conversation with the complete message
      const response = await axios.post(`${backend_url}/api/conversations`, payload, {
        withCredentials: true,
      });
      
      const conversationId = response.data.id;
      
      // Notify parent that conversation was created successfully
      if (onConversationCreated) {
        onConversationCreated(response.data);
      }
      
      // Reset form
      setConversationMessage('');
      setSelectedOtherUserId('');
      setSelectedUserDescription('');
      setObjectLink(preselectedObjectLink || '');
      
      // Reset broker agreement fields if user is a broker
      if (userType === 'broker') {
        setExpectedPrice('');
        setBrokerFee('');
        setContractDuration('');
        setTerminationConditions('');
        setAcceptTerms(false);
      }
      
      // Redirect to the conversation
      history.push(`/conversation/${conversationId}`);
      
    } catch (error) {
      console.error('Error creating conversation:', error);
      setIsLoading(false);
    }
  };

  // Form field styling
  const inputStyle = {
    width: '100%',
    padding: '10px',
    borderRadius: '6px',
    border: '1px solid var(--duck-blue-7)',
    marginBottom: '10px'
  };
  
  const errorStyle = {
    color: 'var(--error-2)',
    fontSize: '0.85rem',
    marginTop: '2px',
    marginBottom: '5px'
  };

  return (
    <div style={{ 
      marginTop: '1rem',
      marginBottom: '2rem'
    }}>
      {userType === 'broker' && preselectedSellerId ? (
        <div style={{ 
          padding: '10px',
          backgroundColor: 'var(--duck-blue-9)',
          borderRadius: '6px',
          marginBottom: '15px'
        }}>
          <p style={{ margin: 0, fontWeight: 'bold' }}>{t('conversation.conversationWithSeller', { id: preselectedSellerId })}</p>
        </div>
      ) : userType === 'seller' && preselectedBrokerId ? (
        <div style={{ 
          padding: '10px',
          backgroundColor: 'var(--duck-blue-9)',
          borderRadius: '6px',
          marginBottom: '15px'
        }}>
          <p style={{ margin: 0, fontWeight: 'bold' }}>{t('conversation.conversationWithBroker', { id: preselectedBrokerId })}</p>
        </div>
      ) : (
        <div style={{ marginBottom: '15px' }}>
          <label style={{ 
            display: 'block', 
            marginBottom: '5px', 
            fontWeight: 'bold', 
            color: 'var(--grey-1)' 
          }}>
            {t('conversation.selectCounterparty')}
          </label>
          <select
            value={selectedOtherUserId}
            onChange={(e) => setSelectedOtherUserId(e.target.value)}
            style={{
              width: '100%',
              padding: '10px',
              borderRadius: '6px',
              border: '1px solid var(--duck-blue-7)',
              backgroundColor: 'white'
            }}
          >
            {otherSideList.map((user) => (
              <option key={user.id} value={user.id}>
                {user.username}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Broker Agreement Details Section (only for brokers) */}
      {userType === 'broker' && (
        <div style={{ 
          marginBottom: '15px',
          padding: '15px',
          backgroundColor: 'var(--duck-blue-10)',
          borderRadius: '8px',
          border: '1px solid var(--duck-blue-7)'
        }}>
          <h3 style={{ 
            marginTop: '0', 
            marginBottom: '15px', 
            color: 'var(--blue-2)',
            borderBottom: '1px solid var(--duck-blue-7)',
            paddingBottom: '8px' 
          }}>
            {t('conversation.agreementTitle')}
          </h3>
          
          <div style={{ marginBottom: '12px' }}>
            <label style={{ 
              display: 'block', 
              marginBottom: '5px', 
              fontWeight: 'bold', 
              color: 'var(--grey-1)' 
            }}>
              {t('conversation.expectedPrice')}*
            </label>
            <input
              type="text"
              value={expectedPrice}
              onChange={handleExpectedPriceChange}
              placeholder={t('conversation.expectedPricePlaceholder')}
              style={{
                ...inputStyle,
                borderColor: formErrors.expectedPrice ? 'var(--error-2)' : inputStyle.border
              }}
            />
            <div style={{
              fontSize: '0.8rem',
              color: expectedPrice.length > 100 ? 'var(--error-2)' : 'var(--grey-5)',
              textAlign: 'right',
              marginTop: '2px'
            }}>
              {expectedPrice.length}/100
            </div>
            {formErrors.expectedPrice && <div style={errorStyle}>{formErrors.expectedPrice}</div>}
          </div>
          
          <div style={{ marginBottom: '12px' }}>
            <label style={{ 
              display: 'block', 
              marginBottom: '5px', 
              fontWeight: 'bold', 
              color: 'var(--grey-1)' 
            }}>
              {t('conversation.brokerFee')}*
            </label>
            <input
              type="text"
              value={brokerFee}
              onChange={handleBrokerFeeChange}
              placeholder={t('conversation.brokerFeePlaceholder')}
              style={{
                ...inputStyle,
                borderColor: formErrors.brokerFee ? 'var(--error-2)' : inputStyle.border
              }}
            />
            <div style={{
              fontSize: '0.8rem',
              color: brokerFee.length > 100 ? 'var(--error-2)' : 'var(--grey-5)',
              textAlign: 'right',
              marginTop: '2px'
            }}>
              {brokerFee.length}/100
            </div>
            {formErrors.brokerFee && <div style={errorStyle}>{formErrors.brokerFee}</div>}
          </div>
          
          <div style={{ marginBottom: '12px' }}>
            <label style={{ 
              display: 'block', 
              marginBottom: '5px', 
              fontWeight: 'bold', 
              color: 'var(--grey-1)' 
            }}>
              {t('conversation.contractDuration')}*
            </label>
            <input
              type="text"
              value={contractDuration}
              onChange={handleContractDurationChange}
              placeholder={t('conversation.contractDurationPlaceholder')}
              style={{
                ...inputStyle,
                borderColor: formErrors.contractDuration ? 'var(--error-2)' : inputStyle.border
              }}
            />
            <div style={{
              fontSize: '0.8rem',
              color: contractDuration.length > 100 ? 'var(--error-2)' : 'var(--grey-5)',
              textAlign: 'right',
              marginTop: '2px'
            }}>
              {contractDuration.length}/100
            </div>
            {formErrors.contractDuration && <div style={errorStyle}>{formErrors.contractDuration}</div>}
          </div>
          
          <div style={{ marginBottom: '12px' }}>
            <label style={{ 
              display: 'block', 
              marginBottom: '5px', 
              fontWeight: 'bold', 
              color: 'var(--grey-1)' 
            }}>
              {t('conversation.termination')}*
            </label>
            <textarea
              value={terminationConditions}
              onChange={handleTerminationConditionsChange}
              placeholder={t('conversation.terminationPlaceholder')}
              style={{
                ...inputStyle,
                height: '80px',
                resize: 'vertical',
                borderColor: formErrors.terminationConditions ? 'var(--error-2)' : inputStyle.border
              }}
              onKeyPress={(e) => {
                // Allow Shift+Enter for line breaks in termination conditions
                if (e.key === 'Enter' && !e.shiftKey) {
                  // Don't prevent default here since this is not a send action
                }
              }}
            />
            <div style={{
              fontSize: '0.8rem',
              color: terminationConditions.length > 1000 ? 'var(--error-2)' : 'var(--grey-5)',
              textAlign: 'right',
              marginTop: '2px'
            }}>
              {terminationConditions.length}/1000
            </div>
            {formErrors.terminationConditions && <div style={errorStyle}>{formErrors.terminationConditions}</div>}
          </div>
          
          <p style={{
            fontSize: '0.9rem',
            fontStyle: 'italic',
            color: 'var(--grey-5)',
            marginTop: '6px',
            marginBottom: '10px'
          }}>
            {t('conversation.agreementNote')}
          </p>

          <div className="checkbox-group" style={{ justifyContent: 'center', marginTop: '6px' }}>
            <input
              type="checkbox"
              id="acceptTerms"
              checked={acceptTerms}
              onChange={(e) => setAcceptTerms(e.target.checked)}
            />
            <label htmlFor="acceptTerms">
              {t('conversation.acceptTerms')}
            </label>
          </div>
          {formErrors.acceptTerms && <div style={{ ...errorStyle, textAlign: 'center' }}>{formErrors.acceptTerms}</div>}
          
        </div>
      )}

      <form onSubmit={handleNewConversation} className="conversation-form">
        <div>
          <label style={{ 
            display: 'block', 
            marginBottom: '5px', 
            fontWeight: 'bold', 
            color: 'var(--grey-1)' 
          }}>
            {t('conversation.startMessage')}
          </label>
          <textarea
            value={conversationMessage}
            style={{ 
              width: '100%', 
              height: '100px',
              padding: '10px',
              borderRadius: '6px',
              border: '1px solid var(--duck-blue-7)',
              resize: 'vertical'
            }}
            onChange={handleMessageChange}
            placeholder={t('conversation.startMessagePlaceholder')}
            onKeyPress={(e) => {
              // Allow Shift+Enter for line breaks
              if (e.key === 'Enter' && !e.shiftKey) {
                // Don't prevent default here since this is not a send action
                // User needs to click the button to send
              }
            }}
          />
          <div style={{
            fontSize: '0.8rem',
            color: conversationMessage.length > 5000 ? 'var(--error-2)' : 'var(--grey-5)',
            textAlign: 'right',
            marginTop: '2px'
          }}>
            {t('conversation.chars', { count: conversationMessage.length })}
          </div>
          {formErrors.message && <div style={errorStyle}>{formErrors.message}</div>}
        </div>

        <div style={{ 
          display: 'flex',
          justifyContent: 'center',
          marginTop: '1.5rem',
        }}>
          <button 
            type="button"
            onClick={onCancel}
            style={{
              padding: '10px 20px',
              backgroundColor: 'var(--grey-3)',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: 'bold',
            }}
          >
            {t('common.cancel')}
          </button>
          <button
            type="submit"
            style={{
              padding: '10px 20px',
              backgroundColor: 'var(--blue-3)',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: 'bold',
            }}
            disabled={isLoading || (userType === 'broker' && !acceptTerms)}
          >
            {isLoading ? t('conversation.sending') : t('conversation.send')}
          </button>
        </div>
      </form>
    </div>
  );
}

export default NewConversation;
